import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { useAuth } from "../context/AuthContext";
import { usePopup } from "../context/PopupContext";
import { logError } from "../utils/logger";
import { userApiService } from "../services/userApiService";
import PopupCenter from "./PopupCenter";
import AgentChatWidget from "./AgentChatWidget";
import AppUpdateBanner from "./AppUpdateBanner";
import { Toaster, toast } from "sonner";
import { isMobileDevice } from "../utils/pwaUtils";
import { initMobilePush } from "../services/pushService";

const NOTIFICATION_POLL_MS = 60000;

/** Actionable alerts that need a sticky popup the user must interact with. */
const STICKY_ALERT_METRICS = new Set([
  "irrigation_consent_pending",
  "firmware",
]);

/** Metrics/types that deserve a live toast popup (skip routine warnings). */
function shouldShowLiveToast(item) {
  const metric = String(item?.metric || "").toLowerCase();
  const type = String(item?.type || "");

  if (STICKY_ALERT_METRICS.has(metric)) return true;
  if (type === "Critical") return true;
  if (type === "Suggestion") return true;
  if (metric === "connectivity") return true;
  if (metric.startsWith("irrigation_")) return true;
  if (metric.startsWith("admin_")) return true;

  return false;
}

function Layout({ children }) {
  const { user } = useAuth();
  const { popups, showPopup, dismissPopup } = usePopup();
  const navigate = useNavigate();
  const seenNotificationIdsRef = useRef(new Set());
  const seededRef = useRef(false);
  const toastDedupeRef = useRef(new Map());

  useEffect(() => {
    if (!user) return;
    initMobilePush(user);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    // Mobile with push permission uses system notifications — skip in-app polling toasts.
    if (isMobileDevice() && typeof Notification !== "undefined" && Notification.permission === "granted") {
      return undefined;
    }

    let active = true;
    let syncFailed = false;

    function showLiveToast(item) {
      const metric = String(item?.metric || "").toLowerCase();
      const dedupeKey = `${item.deviceId}:${metric}:${item.type}`;
      const now = Date.now();
      const lastShown = toastDedupeRef.current.get(dedupeKey) || 0;
      if (now - lastShown < 5 * 60 * 1000) return;
      toastDedupeRef.current.set(dedupeKey, now);

      if (STICKY_ALERT_METRICS.has(metric)) {
        showPopup({
          id: item.id,
          type: "warning",
          title:
            metric === "irrigation_consent_pending"
              ? `Start pump? — ${item.deviceId || "Device"}`
              : `Update available — ${item.deviceId || "Device"}`,
          message: item.message || "Action required.",
          ttlMs: 0,
          action: (
            <button
              type="button"
              className="cta_button solid primary"
              onClick={() => {
                dismissPopup(item.id);
                navigate("/notifications");
              }}
            >
              View
            </button>
          ),
        });
        return;
      }

      const deviceLabel = item.deviceId || "Device";
      const message = item.message || "New notification.";

      if (item.type === "Critical" || metric === "connectivity") {
        toast.error(message, {
          description: deviceLabel,
          duration: 6000,
          action: {
            label: "View",
            onClick: () => navigate("/notifications"),
          },
        });
      } else if (item.type === "Suggestion") {
        toast.info(message, {
          description: `Smart Tip — ${deviceLabel}`,
          duration: 5000,
          action: {
            label: "View",
            onClick: () => navigate("/notifications"),
          },
        });
      } else if (metric.startsWith("admin_")) {
        toast.info(message, {
          description: item.title || "Smart Farming",
          duration: 6000,
          action: {
            label: "View",
            onClick: () => navigate("/notifications"),
          },
        });
      } else {
        toast.warning(message, {
          description: deviceLabel,
          duration: 5000,
          action: {
            label: "View",
            onClick: () => navigate("/notifications"),
          },
        });
      }
    }

    async function pollNotifications() {
      try {
        const data = await userApiService.listNotifications({ limit: 50 });
        if (!active) return;

        const items = data?.items || [];
        if (!seededRef.current) {
          items.forEach((item) => {
            if (item?.id) seenNotificationIdsRef.current.add(item.id);
          });
          seededRef.current = true;
          return;
        }

        items.forEach((item) => {
          const id = item?.id;
          if (!id || seenNotificationIdsRef.current.has(id)) return;
          seenNotificationIdsRef.current.add(id);

          if (!shouldShowLiveToast(item)) return;
          showLiveToast(item);
        });
      } catch (error) {
        if (!active || syncFailed) return;
        syncFailed = true;
        logError("Layout", "Failed to load notifications", error, { uid: user.uid });
      }
    }

    pollNotifications();
    const timer = setInterval(pollNotifications, NOTIFICATION_POLL_MS);

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [dismissPopup, navigate, showPopup, user]);

  return (
    <>
      <Header />
      <main className="pt-[5.75rem] sm:pt-24 bg-background overflow-x-hidden">{children}</main>
      <Footer />
      <PopupCenter popups={popups} onDismiss={dismissPopup} />
      <AppUpdateBanner />
      <AgentChatWidget />
      <Toaster
        position="top-right"
        richColors
        duration={5000}
        closeButton
        toastOptions={{
          className: "font-sans",
          style: { maxWidth: "380px" },
        }}
        visibleToasts={3}
        expand={false}
      />
    </>
  );
}

export default Layout;
